/* ------------------------------------------------------------------
  File: EmptyState.jsx
  Author: Engr. Shaffa K. Kokro Jr. - CEO, Accend Holdings Ltd.
  Last Modified: 2026-05-18
------------------------------------------------------------------ */

// Purpose:
//   - Provides a reusable empty state component for the Accend Holdings website.
//   - Supports icon, eyebrow, title, supporting text, optional action,
//     visual variants, alignment, and dark-surface compatibility.
//   - Uses the acc-empty-state namespace to avoid conflicts with Bootstrap.
//   - Suitable for no results, no articles, no projects, no careers,
//     no resources, and no filtered data states.

/**
 * EmptyState
 *
 * A reusable empty state component for fallback UI.
 *
 * @param {React.ReactNode} icon
 * Optional icon displayed above the message.
 *
 * @param {string} eyebrow
 * Optional small label displayed above the title.
 *
 * @param {string} title
 * Main empty state title.
 *
 * @param {string} text
 * Optional supporting message.
 *
 * @param {React.ReactNode} action
 * Optional action element such as a Button or link.
 *
 * @param {"default" | "soft" | "bordered" | "inverse"} [variant="default"]
 * Controls the visual style of the empty state.
 *
 * @param {"left" | "center"} [align="center"]
 * Controls content alignment.
 *
 * @param {string} [className=""]
 * Additional custom class names.
 */

import "./EmptyState.css";

const EmptyState = ({
  icon,
  eyebrow,
  title = "Nothing to show yet",
  text,
  action,
  variant = "default",
  align = "center",
  className = "",
  ...props
}) => {
  const emptyStateClasses = [
    "acc-empty-state",
    `acc-empty-state--${variant}`,
    `acc-empty-state--${align}`,
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={emptyStateClasses} {...props}>
      <div className="acc-empty-state__inner l-stack l-stack--sm">
        {icon && (
          <span className="acc-empty-state__icon" aria-hidden="true">
            {icon}
          </span>
        )}

        {eyebrow && (
          <span className="acc-empty-state__eyebrow">{eyebrow}</span>
        )}

        <div className="acc-empty-state__content l-stack l-stack--xs">
          {title && <h3 className="acc-empty-state__title">{title}</h3>}

          {text && <p className="acc-empty-state__text">{text}</p>}
        </div>

        {action && <div className="acc-empty-state__action">{action}</div>}
      </div>
    </div>
  );
};

export default EmptyState;